import { HttpClient } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, finalize } from 'rxjs';

import { HttpService } from './http.service';
import { environment } from '@env';
import { ConsoleLogErrorHandler } from '~/v2/core/error-handling/console-log.error-handler';
import { AlertService } from '~/v2/shared/components/alert/alert.service';
import { AuthenticationService } from '~/app/auth/authentication.service';

export abstract class PortalHttpService extends HttpService {
  constructor(
    http: HttpClient,
    alertService: AlertService,
    public auth: AuthenticationService,
    private apiBaseUrl = environment.apiBaseUrl,
  ) {
    super(http, alertService);
  }

  protected override get<T = any>(urlSegment: string, errorHandler: any = null): Observable<T> {
    const handler = errorHandler === null ? this.handleError.bind(this) : errorHandler;
    const uri = `${this.apiBaseUrl}/${urlSegment}`;
    return super.get<T>(uri, handler);
  }

  protected override post<T>(urlSegment: string, body?: any, errorHandler: any = null): Observable<any> {
    const handler = errorHandler === null ? this.handleError.bind(this) : errorHandler;
    const uri = `${this.apiBaseUrl}/${urlSegment}`;
    return super.post<T>(uri, body, handler).pipe(this.loadingIndicator());
  }

  protected override put<T>(urlSegment: string, body?: any, errorHandler: any = null): Observable<any> {
    const handler = errorHandler === null ? this.handleError.bind(this) : errorHandler;
    const uri = `${this.apiBaseUrl}/${urlSegment}`;
    return super.put<T>(uri, body, handler).pipe(this.loadingIndicator());
  }

  protected override delete<T>(urlSegment: string, errorHandler: any = null): Observable<any> {
    const handler = errorHandler === null ? this.handleError.bind(this) : errorHandler;
    const uri = `${this.apiBaseUrl}/${urlSegment}`;
    return super.delete<T>(uri, handler);
  }

  protected override handleError(err: any): Observable<never> {
    if (err.status == 401) {
      this.alertService.error('common.unauthorised');
    } else if (err.status == 403) {
      this.auth.returnToHome();
    } else if (err.error instanceof Array) {
      const errors: { code: string; description: string }[] = err.error;
      for (const item of errors) {
        this.alertService.error(item.code);
      }
    } else {
      this.alertService.error('common.something-went-wrong-try-again-or-contact-us');
    }
    return ConsoleLogErrorHandler.handleError(err);
  }

  private loadingIndicator(): MonoTypeOperatorFunction<void> {
    document.body.classList.add('portal-is-loading');
    return finalize(() => {
      document.body.classList.remove('portal-is-loading');
    });
  }
}
